import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { Spinner } from "@magnetic/spinner";

import { DetailPageFallback } from "src/components/detailPageFallback";
import { BotManagement } from "src/pages/administration/bots/botManagement";
import { UserAndBotLayout } from "src/pages/administration/userAndBotLayout";

import { PATHS } from "./constants";
import { navUrls } from "./navbar";

const DeveloperPage = lazy(() => import("../pages/developer/developerPage"));
const SpyglassPage = lazy(() => import("../pages/spyglass"));
const ApiBearerTokensPage = lazy(
  () => import("src/pages/api-bearer-token/apiBearerTokens")
);
const BgpPeerDetails = lazy(
  () => import("src/pages/vrf/details/menu/bgp/details/BgpPeerDetails")
);
const DynamicBgpPeer = lazy(
  () => import("src/pages/vrf/details/menu/bgp/details/DynamicBgpPeer")
);
const SwitchDetails = lazy(
  () => import("src/pages/switches/details/switchDetails")
);
const SwitchQRRedirectPage = lazy(
  () => import("src/pages/switches/details/QRCode")
);
const FabricDetails = lazy(
  () => import("src/pages/fabrics/details/fabricDetails")
);
const Home = lazy(() => import("../home"));
const AddFabricDesigner = lazy(
  () => import("src/pages/fabricDesigner/addFabricDesigner")
);
const EditFabricDesigner = lazy(
  () => import("src/pages/fabricDesigner/editFabricDesigner")
);
const FabricTemplates = lazy(
  () => import("src/pages/fabricDesigner/templates/fabricTemplates")
);
const FabricListLibrary = lazy(
  () => import("src/pages/fabricList/fabricLibrary")
);
const PortDetails = lazy(() => import("src/pages/ports/details/portDetails"));
const VniDetails = lazy(() => import("src/pages/vni/details/vniDetails"));
const VrfDetails = lazy(() => import("src/pages/vrf/details/vrfDetails"));
const ServerDetails = lazy(
  () => import("src/pages/servers/details/serverDetails")
);
const RackDetails = lazy(() => import("src/pages/racks/details/rackDetails"));
const MultiSiteLibrary = lazy(
  () => import("src/pages/multisite/MultiSiteLibrary")
);
const MultiSiteDetails = lazy(
  () => import("src/pages/multisite/details/MultiSiteDetails")
);
const PortChannelDetails = lazy(
  () => import("src/pages/port-channel/details/portChannelDetails")
);
const PolicyDetails = lazy(
  () => import("src/pages/fabrics/details/bgpPolicy/policyDetails")
);
const ReviewCandidate = lazy(
  () => import("src/pages/candidates/reviewCandidate")
);
const FeaturePreview = lazy(
  () => import("src/pages/featurePreview/featurePreview")
);

const InventoryOverview = lazy(
  () => import("src/pages/inventory/inventoryOverview")
);
const UserManagement = lazy(
  () => import("src/pages/administration/users/userManagement")
);
const Notifications = lazy(
  () => import("src/pages/notifications/notifications")
);
const OrganizationSettings = lazy(
  () => import("src/pages/administration/organization/organizationSettings")
);
const CertManagement = lazy(
  () => import("src/pages/administration/certificates/certificateManagement")
);
const CertServerManagement = lazy(
  () => import("src/pages/administration/certificates/serverCertificates")
);
const SubscriptionOverview = lazy(
  () => import("src/pages/subscriptions/subscriptionOverview")
);
const ServerPortDetails = lazy(
  () => import("src/pages/servers/details/port-details/ServerPortDetails")
);
const MockShellPage = lazy(() => import("src/pages/mock-shell/mockShellPage"));
const Dummy = ({ text }: { text: string }) => text;

export const UIRoutes = () => {
  const monitoredManagedEnabled = window.cnc.settings.get(
    "monitoredManagedSwitch"
  );

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path={`/${PATHS.FABRICS}`}>
          <Route index={true} element={<FabricListLibrary />} />
        </Route>
        <Route path={`/${PATHS.MULTISITE}`}>
          <Route index={true} element={<MultiSiteLibrary />} />
        </Route>
        <Route path={`/${PATHS.MULTISITE}/:dciId`}>
          <Route
            index={true}
            element={
              <Navigate to={PATHS.FABRIC_INTERCONNECTS} replace={true} />
            }
          />
          <Route path={PATHS.CANDIDATE}>
            <Route
              index={true}
              element={<Navigate to={PATHS.REVIEW} replace={true} />}
            />
            <Route path=":candidateTab" element={<ReviewCandidate />} />
          </Route>
          <Route path=":multiSiteTab?" element={<MultiSiteDetails />} />
        </Route>
        <Route path={`/${PATHS.FABRIC}/:fabricId`}>
          <Route path=":fabricTab?" element={<FabricDetails />}>
            <Route index={true} element={<Navigate to={PATHS.SUMMARY} />} />
            <Route path=":name?" element={<FabricDetails />} />
            <Route
              path=":name/:tabId?"
              key="discovery"
              element={<FabricDetails />}
            />
          </Route>
          {/* Underlay BGP peer details - no vrfId in path */}
          <Route
            path={`${PATHS.BGP_PEER}/:bgpPeerId`}
            element={<BgpPeerDetails />}
          />
          <Route path={`${PATHS.VRF}/:vrfId`}>
            <Route path={":vrfTab?"} element={<VrfDetails />} />
            <Route
              path={`${PATHS.BGP_PEER}/:bgpPeerId`}
              element={<BgpPeerDetails />}
            />
            <Route
              path={`${PATHS.DYNAMIC_BGP_PEER}/:deviceId/:peerAddress/:loopBackAddress`}
              element={<DynamicBgpPeer />}
            />
          </Route>
          {monitoredManagedEnabled && (
            <Route path={`${PATHS.MONITORED_VRF}/:vrfId`}>
              <Route path={":vrfTab?"} element={<VrfDetails />} />
            </Route>
          )}
          <Route
            path={`${PATHS.PORT_CHANNEL}/:portChannelId`}
            element={<PortChannelDetails />}
          />
          <Route
            path={`${PATHS.POLICY}/:policyId`}
            element={<PolicyDetails />}
          />
          <Route
            path={`${PATHS.VNI}/:vniId/:vniTab?`}
            element={<VniDetails />}
          />
          {monitoredManagedEnabled && (
            <Route
              path={`${PATHS.MONITORED_VNI}/:vniId/:vniTab?`}
              element={<VniDetails />}
            />
          )}
          <Route
            path={`${PATHS.SERVER}/:serverId/:tabs?`}
            key="serverDetails"
            element={<ServerDetails />}
          >
            <Route
              path="information/:subTabs?"
              key="serverDetails"
              element={<ServerDetails />}
            />
          </Route>
          <Route
            path={`${PATHS.SERVER}/:serverId/${PATHS.PORT}/:portId?`}
            key="serverPortDetails"
            element={<ServerPortDetails />}
          >
            <Route path=":portTab" element={<ServerPortDetails />} />
          </Route>
          <Route path={`${PATHS.RACK}/:rackId`}>
            <Route
              index={true}
              element={<Navigate to="overview" replace={true} />}
            />
            <Route path=":rackTab" element={<RackDetails />} />
          </Route>
          <Route path={PATHS.CHASSIS}>
            <Route path=":nodeId">
              <Route
                index={true}
                element={<Navigate to={PATHS.SUMMARY} replace={true} />}
              />
              <Route path={PATHS.SUMMARY}>
                <Route
                  path=":switchDetailsSubTabs?/:trafficSubTab?"
                  element={<SwitchDetails />}
                />
              </Route>
              <Route path={`${PATHS.PORT}/:portId?`} element={<PortDetails />}>
                <Route path=":portTab" element={<PortDetails />} />
              </Route>
            </Route>
          </Route>
          <Route path={PATHS.CANDIDATE}>
            <Route
              index={true}
              element={<Navigate to={PATHS.REVIEW} replace={true} />}
            />
            <Route path=":candidateTab" element={<ReviewCandidate />} />
          </Route>
          <Route path={PATHS.MOCK_SHELL} element={<MockShellPage />} />
        </Route>
        <Route path={`/${PATHS.FABRIC_TEMPLATES}`} key="fabricTemplates">
          <Route
            index={true}
            element={<Navigate to={PATHS.SWITCH_FABRICS} replace={true} />}
          />
          <Route path={`:tab?`} element={<FabricTemplates />} />
        </Route>
        <Route path={`/${PATHS.FABRIC_DESIGNER}`}>
          <Route path={`:fabricId/edit`} element={<EditFabricDesigner />} />
          <Route path={`:templateId`} element={<AddFabricDesigner />} />
          <Route path="create" element={<AddFabricDesigner />} />
        </Route>
        <Route path={`/${PATHS.SW_QR_CODE}`}>
          <Route path=":serialNumber" element={<SwitchQRRedirectPage />} />
        </Route>
        <Route path={`/${PATHS.DEV}`}>
          <Route
            index={true}
            element={<Navigate to={`/${PATHS.DEV}/echo`} replace={true} />}
          />
          <Route path=":devTab" element={<DeveloperPage />}>
            <Route path=":name" element={<DeveloperPage />} />
          </Route>
        </Route>
        <Route
          path={`/${PATHS.DEVICES}`}
          key="inventory"
          element={<InventoryOverview />}
        />
        <Route
          path={`/${PATHS.INVENTORY_COUNTS}`}
          key="inventoryCounts"
          element={<InventoryOverview />}
        />
        <Route
          path={`/${PATHS.SUBSCRIPTION}/:tab`}
          key="subscription"
          element={<SubscriptionOverview />}
        />
        <Route element={<UserAndBotLayout />}>
          <Route
            path={`/${PATHS.USER_MGMT}`}
            key="userManagement"
            element={<UserManagement />}
          />
          <Route
            path={`/${PATHS.BOT_MGMT}`}
            key="botManagement"
            element={<BotManagement />}
          />
        </Route>
        <Route
          path={`/${PATHS.CERT_MGMT}`}
          key="certManagement"
          element={<CertManagement />}
        />
        <Route
          path={`/${PATHS.CERT_SRVR_MGMT}`}
          key="certServerManagement"
          element={<CertServerManagement />}
        />
        <Route
          path={`/${PATHS.ORG_SETTINGS}`}
          key="OrganizationSettings"
          element={<OrganizationSettings />}
        />
        <Route
          path={`/${PATHS.SPYGLASS}`}
          key="spyglass"
          element={<SpyglassPage />}
        />
        <Route
          path={`/${PATHS.TOKEN_MANAGEMENT}/:name?`}
          key="apiBearerTokens"
          element={<ApiBearerTokensPage />}
        />
        {navUrls.map((to: string) => (
          <Route path={to} key={to} element={<Dummy text={to} />} />
        ))}
        <Route
          path={`/${PATHS.LOGIN}`}
          key="login"
          element={<Navigate to={`/${PATHS.FABRICS}`} replace={true} />}
        />
        <Route
          path={`/${PATHS.DEV_LOGIN}`}
          key="dev-login"
          element={<Navigate to={`/${PATHS.FABRICS}`} replace={true} />}
        />
        <Route path={`/${PATHS.NOTIFICATIONS}`}>
          <Route index={true} element={<Notifications />} />
          <Route
            path=":tab/:name?"
            key="notificationsPolicies"
            element={<Notifications />}
          />
        </Route>
        <Route
          path={`/${PATHS.FEATURE_PREVIEW}`}
          key="featurePreview"
          element={<FeaturePreview />}
        />
        <Route
          path="*"
          element={
            <DetailPageFallback
              breadcrumbs={[["Fabrics", "/fabrics"]]}
              text="Page not found"
            />
          }
        />
      </Routes>
    </Suspense>
  );
};
